import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { requestJson } from "../../../../../helpers/api";

type Coleccion = {
  id: number;
  nombre: string;
  descripcion: string | null;
  orden?: number | null;
  total_articulos?: number;
};

type ArticuloItem = {
  idarticulo: string;
  articulo: string | null;
  familia?: string | null;
  linea?: string | null;
};

type UsuarioItem = {
  id: number;
  nombreUsuario: string;
  correoCorporativo?: string | null;
};

type CollectionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  canView: boolean;
  canEdit: boolean;
  canClone: boolean;
};

const CollectionsModal = ({
  isOpen,
  onClose,
  canView,
  canEdit,
  canClone,
}: CollectionsModalProps) => {
  const [colecciones, setColecciones] = useState<Coleccion[]>([]);
  const [cargandoColecciones, setCargandoColecciones] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription, setCollectionDescription] = useState("");
  const [collectionItems, setCollectionItems] = useState<Set<string>>(new Set());
  const [articulos, setArticulos] = useState<ArticuloItem[]>([]);
  const [articleSearch, setArticleSearch] = useState("");
  const [articleOffset, setArticleOffset] = useState(0);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [saving, setSaving] = useState(false);

  const [usuarios, setUsuarios] = useState<UsuarioItem[]>([]);
  const [usuarioBuscar, setUsuarioBuscar] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [clonando, setClonando] = useState(false);
  const [cloneTargetUserId, setCloneTargetUserId] = useState<number | null>(null);
  const [cloneSelection, setCloneSelection] = useState<Set<number>>(new Set());
  const [cloneSuccessOpen, setCloneSuccessOpen] = useState(false);
  const [cloneSuccessMessage, setCloneSuccessMessage] = useState("");
  const [articleAssignments, setArticleAssignments] = useState<
    Map<string, { coleccionId: number; nombre: string }>
  >(new Map());
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [moveConfirmItem, setMoveConfirmItem] = useState<{
    articuloId: string;
    coleccionId: number;
    coleccionNombre: string;
  } | null>(null);
  const [movingArticle, setMovingArticle] = useState(false);

  const currentUser = useMemo(() => {
    const raw =
      sessionStorage.getItem("authUser") ||
      localStorage.getItem("authUser") ||
      sessionStorage.getItem("user") ||
      localStorage.getItem("user");
    if (!raw) return { id: null as number | null, nombre: "" };
    try {
      const parsed = JSON.parse(raw);
      const id =
        parsed?.usuario?.id ??
        parsed?.user?.id ??
        parsed?.data?.usuario?.id ??
        parsed?.id ??
        null;
      const nombre =
        parsed?.usuario?.nombreUsuario ??
        parsed?.usuario?.nombre ??
        parsed?.user?.nombreUsuario ??
        parsed?.user?.nombre ??
        parsed?.data?.usuario?.nombreUsuario ??
        parsed?.data?.usuario?.nombre ??
        "";
      const num = Number(id);
      return {
        id: Number.isFinite(num) ? num : null,
        nombre: String(nombre ?? ""),
      };
    } catch {
      return { id: null, nombre: "" };
    }
  }, []);

  const isOwnCollections = selectedUserId === currentUser.id;
  const selectedUserLabel = useMemo(() => {
    if (!selectedUserId) return "";
    if (selectedUserId === currentUser.id) return currentUser.nombre || "Mi usuario";
    const match = usuarios.find((u) => u.id === selectedUserId);
    return match?.nombreUsuario ?? "";
  }, [currentUser.id, currentUser.nombre, selectedUserId, usuarios]);
  const selectedCollection = useMemo(
    () => colecciones.find((c) => c.id === selectedCollectionId) ?? null,
    [colecciones, selectedCollectionId],
  );

  const cargarColecciones = async (usuarioId: number | null) => {
    if (!canView || !usuarioId) {
      setColecciones([]);
      return;
    }
    setCargandoColecciones(true);
    try {
      const data = isOwnCollections
        ? await requestJson("/api/informes/colecciones")
        : await requestJson(`/api/informes/colecciones/usuario/${usuarioId}`);
      setColecciones(Array.isArray(data) ? data : []);
    } finally {
      setCargandoColecciones(false);
    }
  };

  const cargarAsignaciones = async (lista: Coleccion[]) => {
    if (!isOwnCollections || lista.length === 0) {
      setArticleAssignments(new Map());
      return;
    }
    const entries = await Promise.all(
      lista.map(async (coleccion) => {
        try {
          const items = await requestJson(
            `/api/informes/colecciones/${coleccion.id}/articulos`,
          );
          const ids = Array.isArray(items) ? items : [];
          return ids.map((id: any) => ({
            articuloId: String(id ?? "").trim(),
            coleccionId: coleccion.id,
            coleccionNombre: coleccion.nombre,
          }));
        } catch {
          return [];
        }
      }),
    );
    const map = new Map<string, { coleccionId: number; nombre: string }>();
    entries.flat().forEach((entry) => {
      if (entry.articuloId.length > 0) {
        map.set(entry.articuloId, {
          coleccionId: entry.coleccionId,
          nombre: entry.coleccionNombre,
        });
      }
    });
    setArticleAssignments(map);
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedUserId(currentUser.id ?? null);
    setCloneTargetUserId(null);
    setCloneSelection(new Set());
    setCloneSuccessOpen(false);
    setCloneSuccessMessage("");
    setSelectedCollectionId(null);
    setCollectionName("");
    setCollectionDescription("");
    setCollectionItems(new Set());
    setArticleSearch("");
  }, [isOpen, currentUser.id]);

  useEffect(() => {
    if (!isOpen || !canClone) return;
    void buscarUsuarios();
  }, [isOpen, canClone]);

  useEffect(() => {
    if (!isOpen || !selectedUserId) return;
    setSelectedCollectionId(null);
    setCloneSelection(new Set());
    setCollectionName("");
    setCollectionDescription("");
    setCollectionItems(new Set());
    void cargarColecciones(selectedUserId);
  }, [isOpen, selectedUserId]);

  useEffect(() => {
    if (!isOwnCollections) {
      setArticleAssignments(new Map());
      return;
    }
    void cargarAsignaciones(colecciones);
  }, [colecciones, isOwnCollections]);

  useEffect(() => {
    if (!selectedCollectionId || !isOwnCollections) {
      setCollectionItems(new Set());
      return;
    }
    const cargar = async () => {
      const items = await requestJson(
        `/api/informes/colecciones/${selectedCollectionId}/articulos`,
      );
      const set = new Set<string>(
        Array.isArray(items) ? items.map((id) => String(id).trim()) : [],
      );
      setCollectionItems(set);
    };
    void cargar();
  }, [selectedCollectionId, isOwnCollections]);

  useEffect(() => {
    if (!isOpen) return;
    setArticleOffset(0);
    setArticulos([]);
  }, [articleSearch, isOpen]);

  const cargarArticulos = async (reset = false) => {
    if (!canView || !isOwnCollections) return;
    setLoadingArticles(true);
    try {
      const offset = reset ? 0 : articleOffset;
      const params = new URLSearchParams();
      if (articleSearch.trim().length > 0) {
        params.set("buscar", articleSearch.trim());
      }
      params.set("limit", "200");
      params.set("offset", String(offset));
      const data = await requestJson(
        `/api/informes/colecciones/articulos/lista?${params.toString()}`,
      );
      const lista = Array.isArray(data) ? (data as ArticuloItem[]) : [];
      if (reset) {
        setArticulos(lista);
        setArticleOffset(lista.length);
      } else {
        setArticulos((prev) => [...prev, ...lista]);
        setArticleOffset((prev) => prev + lista.length);
      }
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !isOwnCollections) return;
    void cargarArticulos(true);
  }, [isOpen, articleSearch, isOwnCollections]);

  const handleSelectCollection = (coleccion: Coleccion) => {
    setSelectedCollectionId(coleccion.id);
    setCollectionName(coleccion.nombre ?? "");
    setCollectionDescription(coleccion.descripcion ?? "");
  };

  const handleToggleArticulo = (id: string) => {
    setCollectionItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleArticuloWithCheck = (id: string) => {
    if (!selectedCollectionId) return;
    const assigned = articleAssignments.get(id);
    if (assigned && assigned.coleccionId !== selectedCollectionId) {
      setMoveConfirmItem({
        articuloId: id,
        coleccionId: assigned.coleccionId,
        coleccionNombre: assigned.nombre,
      });
      setMoveConfirmOpen(true);
      return;
    }
    handleToggleArticulo(id);
  };

  const handleCrear = async () => {
    if (!canEdit || !isOwnCollections || collectionName.trim().length === 0) return;
    const data = (await requestJson("/api/informes/colecciones", {
      method: "POST",
      body: JSON.stringify({
        nombre: collectionName.trim(),
        descripcion: collectionDescription.trim() || null,
      }),
    })) as Coleccion | null;
    if (data?.id) {
      setColecciones((prev) => [...prev, data]);
      setSelectedCollectionId(data.id);
    }
  };

  const handleActualizar = async () => {
    if (!canEdit || !isOwnCollections || !selectedCollectionId) return;
    const data = (await requestJson(
      `/api/informes/colecciones/${selectedCollectionId}`,
      {
        method: "PUT",
        body: JSON.stringify({
          nombre: collectionName.trim(),
          descripcion: collectionDescription.trim() || null,
          orden: selectedCollection?.orden ?? 0,
        }),
      },
    )) as Coleccion | null;
    if (data?.id) {
      setColecciones((prev) =>
        prev.map((c) => (c.id === data.id ? { ...c, ...data } : c)),
      );
    }
  };

  const handleUpdateOrder = async (coleccion: Coleccion, orden: number) => {
    if (!canEdit || !isOwnCollections) return;
    if (
      colecciones.some(
        (c) =>
          c.id !== coleccion.id &&
          c.orden !== null &&
          c.orden !== undefined &&
          c.orden === orden,
      )
    ) {
      alert("Ese número de orden ya está en uso.");
      return;
    }
    const updated = (await requestJson(`/api/informes/colecciones/${coleccion.id}`, {
      method: "PUT",
      body: JSON.stringify({
        nombre: coleccion.nombre,
        descripcion: coleccion.descripcion ?? null,
        orden,
      }),
    })) as Coleccion | null;
    if (updated?.id) {
      setColecciones((prev) =>
        prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
      );
      setSelectedCollectionId(null);
    }
  };

  const handleEliminar = async (id?: number) => {
    if (!canEdit || !isOwnCollections) return;
    const targetId = id ?? selectedCollectionId;
    if (!targetId) return;
    await requestJson(`/api/informes/colecciones/${targetId}`, {
      method: "DELETE",
    });
    setColecciones((prev) => prev.filter((c) => c.id !== targetId));
    if (selectedCollectionId === targetId) {
      setSelectedCollectionId(null);
      setCollectionItems(new Set());
    }
  };

  const handleGuardarArticulos = async () => {
    if (!canEdit || !isOwnCollections || !selectedCollectionId) return;
    setSaving(true);
    try {
      await requestJson(`/api/informes/colecciones/${selectedCollectionId}/articulos`, {
        method: "PUT",
        body: JSON.stringify({ articulos: Array.from(collectionItems) }),
      });
      await cargarAsignaciones(colecciones);
    } finally {
      setSaving(false);
    }
  };

  const buscarUsuarios = async () => {
    if (!canClone) return;
    const params = new URLSearchParams();
    if (usuarioBuscar.trim().length > 0) {
      params.set("buscar", usuarioBuscar.trim());
    }
    const data = await requestJson(`/api/informes/colecciones/usuarios?${params.toString()}`);
    setUsuarios(Array.isArray(data) ? data : []);
  };

  const handleClonar = async (coleccionId: number) => {
    if (!canClone || isOwnCollections) return;
    setClonando(true);
    try {
      await requestJson("/api/informes/colecciones/clonar", {
        method: "POST",
        body: JSON.stringify({ coleccionIdOrigen: coleccionId }),
      });
      if (currentUser.id) {
        setSelectedUserId(currentUser.id);
      }
    } finally {
      setClonando(false);
    }
  };

  const handleToggleCloneSelection = (id: number) => {
    setCloneSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCloneToUser = async () => {
    if (!canClone || !isOwnCollections || !cloneTargetUserId) return;
    const ids = Array.from(cloneSelection);
    if (ids.length === 0) return;
    setClonando(true);
    try {
      const result = await requestJson("/api/informes/colecciones/clonar-usuario", {
        method: "POST",
        body: JSON.stringify({
          usuarioDestinoId: cloneTargetUserId,
          coleccionIds: ids,
        }),
      });
      setCloneSelection(new Set());
      const count = Array.isArray(result) ? result.length : ids.length;
      setCloneSuccessMessage(
        `Se duplicaron ${count} colecciones al usuario seleccionado.`,
      );
      setCloneSuccessOpen(true);
    } finally {
      setClonando(false);
    }
  };

  const handleRemoveFromOther = async (articuloId: string, coleccionId: number) => {
    const items = await requestJson(`/api/informes/colecciones/${coleccionId}/articulos`);
    const list = Array.isArray(items) ? items.map((i) => String(i).trim()) : [];
    const updated = list.filter((id) => id !== articuloId);
    await requestJson(`/api/informes/colecciones/${coleccionId}/articulos`, {
      method: "PUT",
      body: JSON.stringify({ articulos: updated }),
    });
  };

  const handleConfirmMove = async (mode: "remove" | "move") => {
    if (!moveConfirmItem || !selectedCollectionId) return;
    setMovingArticle(true);
    try {
      await handleRemoveFromOther(moveConfirmItem.articuloId, moveConfirmItem.coleccionId);
      if (mode === "move") {
        const next = new Set(collectionItems);
        next.add(moveConfirmItem.articuloId);
        setCollectionItems(next);
        await requestJson(`/api/informes/colecciones/${selectedCollectionId}/articulos`, {
          method: "PUT",
          body: JSON.stringify({ articulos: Array.from(next) }),
        });
      }
      await cargarAsignaciones(colecciones);
    } finally {
      setMovingArticle(false);
      setMoveConfirmOpen(false);
      setMoveConfirmItem(null);
    }
  };

  return (
    <>
      <style>{`
        .collections-modal .modal-dialog {
          max-width: 1320px !important;
          width: 1320px !important;
          margin: 1.75rem auto !important;
        }
        .collections-modal-content {
          width: 1320px !important;
          max-width: 1320px !important;
        }
        @media (max-width: 1250px) {
          .collections-modal .modal-dialog {
            max-width: 98vw !important;
            width: 98vw !important;
            margin: 1rem auto !important;
          }
          .collections-modal-content {
            width: 98vw !important;
            max-width: 98vw !important;
          }
        }
        .collections-modal .modal-content {
          max-height: 90vh;
        }
        .collections-modal .modal-body {
          overflow-y: auto;
        }
        .collections-sticky-table thead th {
          position: sticky;
          top: 0;
          z-index: 9;
          background-color: var(--vz-secondary-bg, var(--vz-light));
          font-size: 0.925rem !important;
          line-height: 1.05 !important;
          padding: 0.42rem 0.55rem !important;
        }
        .collections-sticky-table thead th:first-child {
          left: 0;
          z-index: 11;
          box-shadow: 1px 0 0 var(--vz-border-color);
        }
        .collections-sticky-table tbody td {
          font-size: 0.925rem !important;
          line-height: 1.06 !important;
          padding: 0.42rem 0.55rem !important;
        }
        .collections-sticky-table tbody td:first-child:not([colspan]) {
          position: sticky;
          left: 0;
          z-index: 6;
          background-color: var(--vz-body-bg);
          box-shadow: 1px 0 0 var(--vz-border-color);
        }
        .collections-sticky-table tbody tr.table-active td:first-child:not([colspan]) {
          background-color: var(--vz-secondary-bg, var(--vz-light));
        }
      `}</style>
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      className="modal-xxl collections-modal"
      contentClassName="collections-modal-content"
    >
      <Modal isOpen={moveConfirmOpen} toggle={() => setMoveConfirmOpen(false)}>
        <ModalHeader toggle={() => setMoveConfirmOpen(false)}>
          Artículo ya asignado
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            El artículo ya pertenece a la colección{" "}
            <strong>{moveConfirmItem?.coleccionNombre}</strong>.
          </p>
          <p className="mb-3">¿Qué querés hacer?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="light"
              className="border"
              onClick={() => setMoveConfirmOpen(false)}
              disabled={movingArticle}
            >
              Cancelar
            </Button>
            <Button
              color="warning"
              onClick={() => handleConfirmMove("remove")}
              disabled={movingArticle}
            >
              Quitar de {moveConfirmItem?.coleccionNombre}
            </Button>
            <Button
              color="primary"
              onClick={() => handleConfirmMove("move")}
              disabled={movingArticle}
            >
              Mover a esta colección
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <Modal isOpen={cloneSuccessOpen} toggle={() => setCloneSuccessOpen(false)}>
        <ModalHeader toggle={() => setCloneSuccessOpen(false)}>
          Duplicación completada
        </ModalHeader>
        <ModalBody>
          {cloneSuccessMessage}
          <div className="mt-3 text-end">
            <Button color="primary" onClick={() => setCloneSuccessOpen(false)}>
              Listo
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <ModalHeader toggle={onClose}>Colecciones de artículos</ModalHeader>
      <ModalBody>
        {!canView ? (
          <div className="alert alert-warning">No tenés permisos para ver colecciones.</div>
        ) : (
          <Row className="g-3">
            <Col lg={5}>
              {canClone && (
                <FormGroup>
                  <Label>Usuario</Label>
                  <div className="d-flex gap-2">
                    <Input
                      value={usuarioBuscar}
                      onChange={(e) => setUsuarioBuscar(e.target.value)}
                      placeholder="Nombre o correo"
                    />
                    <Button color="light" className="border" onClick={buscarUsuarios}>
                      Buscar
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Input
                      type="select"
                      value={selectedUserId ?? ""}
                      onChange={(e) => setSelectedUserId(Number(e.target.value) || null)}
                    >
                      <option value="">Seleccioná usuario</option>
                      {currentUser.id ? (
                        <option value={currentUser.id}>
                          {currentUser.nombre || "Mi usuario"}
                        </option>
                      ) : null}
                      {usuarios.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombreUsuario}
                        </option>
                      ))}
                    </Input>
                  </div>
                </FormGroup>
              )}
              {!canClone && (
                <div className="mb-2 text-muted">
                  Mostrando tus colecciones
                </div>
              )}
              <div className="table-responsive">
                <Table className="table-sm align-middle mb-0 collections-sticky-table">
                  <thead className="table-light">
                    <tr>
                      {canClone && isOwnCollections ? (
                        <th style={{ width: 40 }}>
                          <Input
                            type="checkbox"
                            checked={cloneSelection.size > 0 && cloneSelection.size === colecciones.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCloneSelection(new Set(colecciones.map((c) => c.id)));
                              } else {
                                setCloneSelection(new Set());
                              }
                            }}
                          />
                        </th>
                      ) : null}
                      <th>Colección</th>
                      <th>Descripción</th>
                      <th>Usuario</th>
                      <th className="text-center" style={{ width: 90 }}>
                        Orden
                      </th>
                      <th className="text-center" style={{ width: 110 }}>
                        Artículos
                      </th>
                      <th className="text-end" style={{ width: 180 }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cargandoColecciones ? (
                      <tr>
                        <td colSpan={canClone && isOwnCollections ? 7 : 6}>
                          <Spinner size="sm" /> Cargando...
                        </td>
                      </tr>
                    ) : colecciones.length === 0 ? (
                      <tr>
                        <td colSpan={canClone && isOwnCollections ? 7 : 6} className="text-muted">
                          Sin colecciones.
                        </td>
                      </tr>
                    ) : (
                      colecciones.map((c) => (
                        <tr
                          key={c.id}
                          className={selectedCollectionId === c.id ? "table-active" : ""}
                        >
                          {canClone && isOwnCollections ? (
                            <td>
                              <Input
                                type="checkbox"
                                checked={cloneSelection.has(c.id)}
                                onChange={() => handleToggleCloneSelection(c.id)}
                              />
                            </td>
                          ) : null}
                          <td>{c.nombre}</td>
                          <td className="text-muted">{c.descripcion ?? "—"}</td>
                          <td>{selectedUserLabel || "—"}</td>
                          <td className="text-center">
                            {isOwnCollections ? (
                              <Input
                                type="number"
                                min={0}
                                value={c.orden ?? ""}
                                disabled={selectedCollectionId !== c.id}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const value = raw === "" ? null : Number(raw);
                                  setColecciones((prev) =>
                                    prev.map((item) =>
                                      item.id === c.id
                                        ? {
                                            ...item,
                                            orden: value !== null && Number.isFinite(value)
                                              ? value
                                              : null,
                                          }
                                        : item,
                                    ),
                                  );
                                }}
                                className="form-control form-control-sm text-center"
                                style={{ maxWidth: 80 }}
                              />
                            ) : (
                              c.orden ?? ""
                            )}
                          </td>
                          <td className="text-center">{c.total_articulos ?? "-"}</td>
                          <td className="text-end">
                            {isOwnCollections ? (
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  size="sm"
                                  color="light"
                                  className="border"
                                  onClick={() => handleSelectCollection(c)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  color="light"
                                  className="border"
                                  disabled={selectedCollectionId !== c.id}
                                  onClick={() => handleUpdateOrder(c, c.orden ?? 0)}
                                >
                                  Guardar
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => handleEliminar(c.id)}
                                  disabled={!canEdit}
                                >
                                  Eliminar
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                color="primary"
                                onClick={() => handleClonar(c.id)}
                                disabled={clonando}
                              >
                                {clonando ? "Clonando..." : "Clonar"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {canClone && isOwnCollections ? (
                <div className="mt-3 border-top pt-3">
                  <Label className="mb-2 text-uppercase text-muted small">
                    Duplicar colecciones seleccionadas
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Input
                      type="select"
                      value={cloneTargetUserId ?? ""}
                      onChange={(e) => setCloneTargetUserId(Number(e.target.value) || null)}
                      style={{ minWidth: 240 }}
                    >
                      <option value="">Seleccioná usuario destino</option>
                      {usuarios
                        .filter((u) => u.id !== currentUser.id)
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.nombreUsuario}
                          </option>
                        ))}
                    </Input>
                    <Button
                      color="primary"
                      onClick={handleCloneToUser}
                      disabled={clonando || !cloneTargetUserId || cloneSelection.size === 0}
                    >
                      {clonando ? "Duplicando..." : "Duplicar a usuario"}
                    </Button>
                    <span className="text-muted">
                      Seleccionadas: {cloneSelection.size}
                    </span>
                  </div>
                </div>
              ) : null}
            </Col>
            <Col lg={7}>
              {!isOwnCollections ? (
                <div className="alert alert-info mb-0">
                  Seleccioná una colección para clonarla a tu usuario.
                </div>
              ) : (
                <>
                  <FormGroup>
                    <Label>Nombre</Label>
                    <Input
                      value={collectionName}
                      onChange={(e) => setCollectionName(e.target.value)}
                      disabled={!canEdit}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Descripción</Label>
                    <Input
                      value={collectionDescription}
                      onChange={(e) => setCollectionDescription(e.target.value)}
                      disabled={!canEdit}
                    />
                  </FormGroup>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button color="primary" onClick={handleCrear} disabled={!canEdit}>
                      Crear
                    </Button>
                    <Button color="secondary" onClick={handleActualizar} disabled={!canEdit}>
                      Guardar
                    </Button>
                    <Button
                      color="danger"
                      onClick={() => handleEliminar()}
                      disabled={!canEdit || !selectedCollectionId}
                    >
                      Eliminar
                    </Button>
                  </div>
                  <FormGroup>
                    <Label>Buscar artículo</Label>
                    <Input
                      value={articleSearch}
                      onChange={(e) => setArticleSearch(e.target.value)}
                      placeholder="ID o descripción"
                    />
                  </FormGroup>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Button
                      color="light"
                      className="border"
                      onClick={() => cargarArticulos(true)}
                    >
                      Buscar
                    </Button>
                    <Button
                      color="success"
                      onClick={handleGuardarArticulos}
                      disabled={!canEdit || saving || !selectedCollectionId}
                    >
                      {saving ? "Guardando..." : "Guardar artículos"}
                    </Button>
                    <span className="text-muted">
                      Seleccionados: {collectionItems.size}
                    </span>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: 260 }}>
                    <Table className="table-sm align-middle mb-0 collections-sticky-table">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 40 }}>
                            <Input
                              type="checkbox"
                              checked={
                                articulos.length > 0 &&
                                articulos.every((art) => collectionItems.has(art.idarticulo))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const next = new Set(collectionItems);
                                  articulos.forEach((art) => {
                                    const assigned = articleAssignments.get(art.idarticulo);
                                    if (
                                      !assigned ||
                                      assigned.coleccionId === selectedCollectionId
                                    ) {
                                      next.add(art.idarticulo);
                                    }
                                  });
                                  setCollectionItems(next);
                                } else {
                                  setCollectionItems(new Set());
                                }
                              }}
                            />
                          </th>
                          <th>ID</th>
                          <th>Descripción</th>
                          <th>Familia</th>
                          <th>Línea</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articulos.map((art) => (
                          <tr key={art.idarticulo}>
                            <td>
                              {(() => {
                                const assigned = articleAssignments.get(art.idarticulo);
                                const assignedToOther =
                                  assigned &&
                                  assigned.coleccionId !== selectedCollectionId;
                                return (
                              <Input
                                type="checkbox"
                                checked={collectionItems.has(art.idarticulo)}
                                onChange={() => handleToggleArticuloWithCheck(art.idarticulo)}
                                disabled={!selectedCollectionId || !!assignedToOther}
                              />
                                );
                              })()}
                            </td>
                            <td>{art.idarticulo}</td>
                            <td>
                              {art.articulo ?? ""}
                              {(() => {
                                const assigned = articleAssignments.get(art.idarticulo);
                                const assignedToOther =
                                  assigned &&
                                  assigned.coleccionId !== selectedCollectionId;
                                if (!assignedToOther) return null;
                                return (
                                  <div className="small text-muted mt-1 d-flex align-items-center gap-2">
                                    En {assigned?.nombre}
                                    <Button
                                      size="sm"
                                      color="link"
                                      className="p-0 text-decoration-none"
                                      onClick={() => handleToggleArticuloWithCheck(art.idarticulo)}
                                    >
                                      Cambiar
                                    </Button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td>{art.familia ?? "—"}</td>
                            <td>{art.linea ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-2">
                    <Button color="light" className="border" onClick={() => cargarArticulos()}>
                      {loadingArticles ? "Cargando..." : "Cargar más"}
                    </Button>
                  </div>
                </>
              )}
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
    </>
  );
};

export default CollectionsModal;
